/**
 * Event sync engine (#1950 Phase 1).
 *
 * Drains the IndexedDB ``event_queue`` (see ``utils/eventLog.ts``) up to
 * the server's ``POST /api/projects/events/`` endpoint. Runs as a
 * top-level singleton mounted from main.tsx/App.tsx via
 * ``initEventSync()`` — the drain ticks on:
 *
 *   - explicit nudge via ``window.dispatchEvent('rdm:event-enqueued')``
 *     (fired from utils/eventLog.ts after each successful emit)
 *   - the standard ``online`` event when connectivity returns
 *   - ``visibilitychange`` when the tab becomes visible again
 *   - a background poll (default 5s) while online + visible
 *
 * One drain runs at a time globally (module-level lock). Per-event
 * statuses from the server are mapped to queue actions:
 *
 *   - ``created`` / ``duplicate`` → delete from queue
 *   - ``invalid``                  → mark as 'error' (terminal — don't retry)
 *   - ``error``                    → mark for retry (transient, exponential backoff)
 *
 * Locked-in contract: this engine is the ONLY path that talks to the
 * events endpoint. Direct callers of ``emit()`` never see the network.
 */

import { eventsApi, ApiError, type EventBatchResponse, type EventPayloadForServer } from './api';
import {
  deleteEvents,
  emit as emitEvent,
  getPendingEvents,
  markEventsFailed,
  markEventsRetry,
  markEventsSyncing,
  requeueErroredEvents,
  type EventQueueRecord,
} from '../utils/eventLog';

// ---------------------------------------------------------------------
// Tuning knobs. The defaults match the design contract:
//   - BATCH_SIZE: up to 50 events per POST (server allows 200)
//   - DRAIN_TICK_MS: 5s background poll while active
//   - RETRY_THROTTLE_MS: don't re-attempt a row that failed within 8s
// ---------------------------------------------------------------------
const BATCH_SIZE = 50;
const DRAIN_TICK_MS = 5_000;
const RETRY_THROTTLE_MS = 8_000;

let drainInFlight = false;
let initialized = false;
let tickHandle: number | null = null;

// Offline → online transition tracking for app_resumed_from_offline.
// Set to the offline-event timestamp when navigator goes offline; cleared
// after the resume event fires (or on init while online so the FIRST
// online event after install doesn't spuriously fire one).
let wentOfflineAtMs: number | null = null;
let pendingResumeEvent = false;

export interface DrainSummary {
  attempted: number;
  created: number;
  duplicate: number;
  invalid: number;
  errored: number;
  retried: number;
  skipped: number;
}

const EMPTY_SUMMARY: DrainSummary = {
  attempted: 0,
  created: 0,
  duplicate: 0,
  invalid: 0,
  errored: 0,
  retried: 0,
  skipped: 0,
};

function toServerPayload(row: EventQueueRecord): EventPayloadForServer {
  // Only send fields the server's EventInputSerializer accepts. Local
  // bookkeeping (sync_status, sync_attempts, queued_at, ...) stays in IDB.
  return {
    client_event_id: row.client_event_id,
    event_type: row.event_type,
    event_at: row.event_at,
    store_id: row.store_id,
    route_id: row.route_id,
    project_id: row.project_id,
    calllog_id: row.calllog_id,
    correction_of: row.correction_of,
    correction_of_client_event_id: row.correction_of_client_event_id ?? null,
    payload: row.payload,
  };
}

/**
 * Process pending events from IDB up to BATCH_SIZE per batch. Returns
 * a summary of what happened. Safe to call concurrently — second caller
 * sees ``drainInFlight=true`` and immediately returns EMPTY_SUMMARY.
 */
export async function drainEventQueue(): Promise<DrainSummary> {
  if (drainInFlight) return EMPTY_SUMMARY;
  drainInFlight = true;
  try {
    return await drainInner();
  } finally {
    drainInFlight = false;
  }
}

async function drainInner(): Promise<DrainSummary> {
  const summary: DrainSummary = { ...EMPTY_SUMMARY };

  const pending = await getPendingEvents(BATCH_SIZE);
  if (pending.length === 0) return summary;

  // Throttle rows that failed very recently so a flapping network can't
  // burn through retry attempts in a hot loop.
  const now = Date.now();
  const eligible = pending.filter((row) => {
    if (!row.last_attempt_at) return true;
    const lastMs = new Date(row.last_attempt_at).getTime();
    if (now - lastMs >= RETRY_THROTTLE_MS) return true;
    summary.skipped += 1;
    return false;
  });
  if (eligible.length === 0) return summary;

  summary.attempted = eligible.length;
  const eligibleIds = eligible.map((row) => row.client_event_id);

  // Mark as 'syncing' BEFORE the network call so a concurrent drain
  // (shouldn't happen, but defense in depth) can't grab the same rows.
  await markEventsSyncing(eligibleIds);

  let response: EventBatchResponse;
  try {
    response = await eventsApi.postBatch(eligible.map(toServerPayload));
  } catch (err) {
    // Transient failure (offline, DNS, timeout, or an HTTP error thrown by
    // apiFetch — 5xx / 503 maintenance). Keep the rows 'pending' so they retry
    // when connectivity returns. Capture a fallback message: an ApiError's own
    // message is often EMPTY over HTTP/2 (statusText is nulled), so fall back to
    // the status code rather than storing a blank last_error.
    let message = err instanceof Error ? err.message : String(err);
    if (!message && err instanceof ApiError) message = `HTTP ${err.status}`;
    if (!message) message = 'network error';
    await markEventsRetry(eligibleIds, message);
    summary.retried += eligibleIds.length;
    return summary;
  }

  // Build a lookup of the syncing rows by id for fast access below.
  const eligibleById = new Map(eligible.map((r) => [r.client_event_id, r]));

  // Bucket per-event statuses into single queue-state transitions.
  const toDelete: string[] = [];
  const toRetry: { ids: string[]; message?: string } = { ids: [] };
  const toFail: { ids: string[]; message?: string } = { ids: [] };

  for (const result of response.results ?? []) {
    const cid = result.client_event_id;
    if (!cid || !eligibleById.has(cid)) continue;
    switch (result.status) {
      case 'created':
        summary.created += 1;
        toDelete.push(cid);
        break;
      case 'duplicate':
        summary.duplicate += 1;
        toDelete.push(cid);
        break;
      case 'invalid':
        summary.invalid += 1;
        toFail.ids.push(cid);
        if (!toFail.message) {
          toFail.message = JSON.stringify(result.errors ?? { reason: 'invalid' });
        }
        break;
      case 'error':
      default:
        summary.errored += 1;
        toRetry.ids.push(cid);
        if (!toRetry.message) toRetry.message = 'server-side error';
        break;
    }
  }

  // Apply transitions. deleteEvents + markEventsFailed + markEventsRetry
  // all tolerate missing rows gracefully.
  if (toDelete.length > 0) await deleteEvents(toDelete);
  if (toFail.ids.length > 0) await markEventsFailed(toFail.ids, toFail.message);
  if (toRetry.ids.length > 0) await markEventsRetry(toRetry.ids, toRetry.message);

  // Surface queue changes so UI listeners (e.g. status indicator) refresh.
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('rdm:event-queue-changed'));
  }

  // app_resumed_from_offline (#1950): a successful drain just landed AFTER
  // the rep was offline. Emit one synthetic event with the offline window
  // duration + the size of the backlog that drained. We only fire once
  // per offline → online transition; the flag clears immediately.
  if (pendingResumeEvent && (toDelete.length > 0 || toFail.ids.length > 0)) {
    const offlineDurationMs = wentOfflineAtMs ? Date.now() - wentOfflineAtMs : null;
    pendingResumeEvent = false;
    wentOfflineAtMs = null;
    void emitEvent({
      event_type: 'app_resumed_from_offline',
      event_at: new Date(),
      payload: {
        offline_duration_ms: offlineDurationMs,
        offline_duration_minutes:
          offlineDurationMs !== null ? Math.round(offlineDurationMs / 60000) : null,
        drained_events: toDelete.length,
        failed_events: toFail.ids.length,
      },
    });
  }

  // If we just drained a full batch, there's likely more — schedule an
  // immediate follow-up so a backlog from a long offline window clears
  // promptly without waiting for the next periodic tick.
  if (pending.length === BATCH_SIZE) {
    setTimeout(() => {
      void drainEventQueue();
    }, 0);
  }

  return summary;
}

export const REQUEUE_ONCE_FLAG = 'rdm_event_error_requeue_v1';

/**
 * One-time recovery of events parked as terminal 'error' by the OLD permanent-
 * park behavior, so a rep's pre-update backlog self-heals on first launch — no
 * "tap Sync Now" needed. Guarded by a localStorage flag so it runs ONCE per
 * device: after that, a genuinely-'invalid' event stays parked instead of being
 * re-looped on every launch. Returns how many rows were revived (0 if already
 * run). Best-effort — never throws.
 */
export async function recoverParkedEventsOnce(): Promise<number> {
  try {
    if (typeof localStorage !== 'undefined' && localStorage.getItem(REQUEUE_ONCE_FLAG)) return 0;
    const revived = await requeueErroredEvents();
    try {
      localStorage?.setItem(REQUEUE_ONCE_FLAG, '1');
    } catch {
      /* private-mode / storage full — recovery still ran */
    }
    return revived;
  } catch {
    return 0;
  }
}

/**
 * Mount the singleton sync engine listeners. Idempotent — calling
 * twice is a no-op. Call once from the app entry point (App.tsx or
 * main.tsx) after authentication is ready, since unauthenticated
 * requests will 401 and uselessly bump retry counters.
 */
export function initEventSync(): void {
  if (initialized) return;
  initialized = true;

  const triggerDrain = () => {
    void drainEventQueue();
  };

  const onEnqueued = () => triggerDrain();
  const onOnline = () => {
    // Coming back online — flag the next successful drain to emit the
    // app_resumed_from_offline event (carries the offline window
    // duration + backlog size). wentOfflineAtMs is the start timestamp.
    pendingResumeEvent = true;
    triggerDrain();
  };
  const onOffline = () => {
    if (wentOfflineAtMs === null) wentOfflineAtMs = Date.now();
  };
  const onVisibility = () => {
    if (document.visibilityState === 'visible') triggerDrain();
  };

  window.addEventListener('rdm:event-enqueued', onEnqueued);
  window.addEventListener('online', onOnline);
  window.addEventListener('offline', onOffline);
  document.addEventListener('visibilitychange', onVisibility);

  // If the page loads while already offline, prime the timestamp so the
  // first online event still produces a meaningful offline_duration.
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    wentOfflineAtMs = Date.now();
  }

  // Periodic tick while online + visible. setInterval handle kept so a
  // future shutdownEventSync() can clear it cleanly.
  tickHandle = window.setInterval(() => {
    if (navigator.onLine && document.visibilityState === 'visible') {
      triggerDrain();
    }
  }, DRAIN_TICK_MS);

  // Recover any pre-update backlog, then do the initial drain on mount so the
  // revived rows are picked up in the same cycle (also flushes anything queued
  // from a prior session). Best-effort — recovery must not block draining.
  void recoverParkedEventsOnce()
    .catch(() => undefined)
    .then(() => triggerDrain());
}

/** Tear down the sync engine listeners. Used by tests + the auth flow
 *  when a user signs out (so we don't push the signed-out user's queued
 *  events under the next user's token). */
export function shutdownEventSync(): void {
  if (!initialized) return;
  initialized = false;
  // Listener cleanup: we use named handlers above only inside initEventSync,
  // so we re-add no-ops here and rely on the page reload after sign-out to
  // fully reset. This keeps the engine simple and matches the
  // useExpenseQueue precedent.
  if (tickHandle !== null) {
    clearInterval(tickHandle);
    tickHandle = null;
  }
}
